/**
 * Strings for the back-office additions of September 2026: field-event
 * register (Registro de eventos), internal MACQR form, projects/channels.
 * Merged into the main dictionaries by uiStrings.ts. Spanish is the source.
 */
import type { Locale } from '@/types/api.types';

const es: Record<string, string> = {
  // Home — partner staff entry points (no login)
  'home.staff.heading': 'Equipo técnico y organizaciones socias',
  'home.staff.intro': 'Registro de eventos de campo y de solicitudes recibidas por otros canales (buzón, libro de reclamaciones, formulario físico). Solo se pide tu correo electrónico.',
  'home.staff.event': 'Registrar un evento',
  'home.staff.internal': 'Registrar una solicitud recibida por otro canal',
  'home.staff.access': 'La revisión y el seguimiento se hacen en el back-office (acceso con cuenta Microsoft del proyecto).',
  'fev.reporterEmail': 'Tu correo electrónico',
  'fev.reporterEmailHint': 'Correo de la persona que reporta (equipo técnico ACOPAGRO, ORO VERDE, PUR, CECAT, VOLCAFE).',
  'fev.public.saved': 'Evento registrado. Puedes adjuntar los medios de verificación durante las próximas 24 horas.',
  'fev.public.another': 'Registrar otro evento',
  // ── Back-office: navigation & shared ──
  'admin.events': 'Eventos',
  'admin.newEvent': 'Nuevo evento',
  'admin.newInternalRequest': 'Registrar solicitud (otro canal)',
  'admin.export': 'Exportar Excel',
  'admin.exporting': 'Generando el archivo…',
  'admin.delete': 'Eliminar',
  'admin.confirmDelete': '¿Eliminar este registro? Esta acción no se puede deshacer.',
  'admin.created': 'Registro creado',
  'admin.back': 'Volver',
  'admin.col.project': 'Proyecto',
  'admin.col.org': 'Entidad',
  'admin.col.component': 'Componente',
  'admin.col.dates': 'Fechas',
  'admin.col.name': 'Nombre',
  'admin.col.participants': 'Participantes',
  'admin.col.channel': 'Canal',
  'admin.filters.from': 'Desde',
  'admin.filters.to': 'Hasta',
  'admin.stats.events': 'Eventos',
  'admin.stats.participants': 'Participantes',
  'admin.stats.hours': 'Horas efectivas',
  'admin.stats.byComponent': 'Por componente',
  'admin.stats.byOrg': 'Por entidad',

  // Organisations, channels
  'org.ACOPAGRO': 'ACOPAGRO',
  'org.ORO_VERDE': 'ORO VERDE',
  'org.PUR': 'PUR',
  'org.CECAT': 'CECAT',
  'org.VOLCAFE': 'VOLCAFE',
  'channel.ONLINE_FORM': 'Formulario en línea',
  'channel.COMPLAINTS_BOOK': 'Libro de reclamaciones (organizaciones socias o viveros)',
  'channel.MAILBOX': 'Buzón MACQR (organizaciones socias o viveros)',
  'channel.EVENT_BOX': 'Caja MACQR en eventos (talleres o reuniones)',
  'channel.PAPER_FORM': 'Formulario físico recogido en visita de campo',
  'channel.WEBSITE_EMAIL': 'Sitio web o correo institucional',

  // Field events — enums
  'component.SOCIALIZACION': 'Socialización',
  'component.SIEMBRA': 'Siembra',
  'component.MANEJO_FORESTAL': 'Manejo forestal',
  'component.GESTION_TECNICA': 'Gestión técnica del proyecto',
  'socialization.GRUPAL': 'Grupal',
  'socialization.INDIVIDUAL': 'Individual',
  'technical.COMITE_FORESTAL': 'Comité forestal',
  'technical.TALLER_CAPACITACION': 'Taller de capacitación',
  'technical.REUNION_TECNICA': 'Reunión técnica y/o monitoreo',
  'technical.CARBON_REVENUE_SHARING': 'Carbon Revenue Sharing',
  'forestPlan.CAPACITACION_BRIGADA': 'Capacitación a brigada forestal',
  'forestPlan.CAPACITACION_SAF': 'Capacitación en manejo de SAF',
  'modality.PRESENCIAL': 'Presencial',
  'modality.VIRTUAL': 'Virtual',
  'modality.AMBOS': 'Ambos',
  'attachKind.ATTENDANCE_LIST': 'Lista de asistencia',
  'attachKind.PHOTO': 'Fotos del evento',
  'attachKind.VIDEO': 'Video del evento',
  'attachKind.DOCUMENT': 'Actas, informes o documentos',
  'attachKind.SOCIALIZATION_SHEET': 'Ficha de socialización (visita individual)',

  // Field events — form
  'fev.heading.new': 'Registrar un evento',
  'fev.heading.edit': 'Evento',
  'fev.intro': 'Solo se debe reportar un evento finalizado. Ten a mano la lista de asistencia escaneada, máximo 3 fotos y, si aplica, el video o el acta.',
  'fev.section.general': 'Datos generales del evento',
  'fev.section.report': 'Reporte del evento',
  'fev.section.location': 'Localización del evento',
  'fev.section.details': 'Detalles del evento',
  'fev.section.participants': 'Participantes',
  'fev.section.team': 'Participación del equipo técnico del proyecto',
  'fev.section.conclusions': 'Desarrollo del evento',
  'fev.section.attachments': 'Medios de verificación',
  'fev.project': 'Proyecto',
  'fev.reportingOrg': 'Entidad que reporta',
  'fev.contractWave': 'Contrato u ola relacionada al evento (año)',
  'fev.component': 'Evento relacionado a',
  'fev.socializationType': 'Tipo de socialización',
  'fev.technicalType': 'Tipo de evento: gestión técnica del proyecto',
  'fev.forestPlanType': 'Tipo de evento: plan de manejo forestal',
  'fev.startDate': 'Fecha de inicio del evento',
  'fev.endDate': 'Fecha de término del evento',
  'fev.modality': 'Modalidad de ejecución',
  'fev.province': 'Provincia',
  'fev.district': 'Distrito',
  'fev.sector': 'Sector o comunidad',
  'fev.sectorHint': 'Señala la comunidad o sector donde se desarrolló el evento.',
  'fev.otherPlace': 'Otro lugar, detallar',
  'fev.name': 'Nombre del evento',
  'fev.effectiveHours': 'Horas efectivas del evento',
  'fev.effectiveHoursHint': 'Total de horas efectivas según la agenda. Solo el número (ej. 2.5).',
  'fev.objective': 'Objetivo del evento',
  'fev.participantsTotal': 'N° total de participantes',
  'fev.participantsWomen': 'N° total de mujeres',
  'fev.participantsMen': 'N° total de hombres',
  'fev.participantsOther': 'N° total de otros géneros',
  'fev.participantsHint': 'Mujeres + hombres + otros géneros debe ser igual al total.',
  'fev.teamHelp': 'Miembros del equipo técnico del proyecto (todas las entidades) que participaron en el evento. Ya están incluidos en el total de participantes.',
  'fev.teamWomen': 'Mujeres',
  'fev.teamMen': 'Hombres',
  'fev.teamTotal': 'Total del equipo técnico',
  'fev.conclusions': 'Conclusiones, acuerdos o recomendaciones',
  'fev.conclusionsHint': 'De manera concisa: conclusiones principales, acuerdos alcanzados, recomendaciones de los participantes.',
  'fev.attachments.help': 'PDF, JPG o PNG hasta 10 MB; video MP4 hasta 100 MB. También puedes referenciar un archivo de Google Drive con su enlace.',
  'fev.attachments.kind': 'Tipo de documento',
  'fev.attachments.upload': 'Subir archivo',
  'fev.attachments.addLink': 'Agregar enlace',
  'fev.attachments.linkUrl': 'Enlace (https://…)',
  'fev.attachments.saveFirst': 'Guarda el evento para poder adjuntar documentos.',
  'fev.attachments.empty': 'Sin documentos adjuntos.',
  'fev.attachments.open': 'Abrir',
  'fev.reportedBy': 'Registrado por',
  'fev.legacy': 'Importado del formulario de Google (registro original: {email}, {date})',
  'fev.save': 'Guardar evento',
  'fev.saved': 'Evento guardado',

  // Internal MACQR form
  'internal.heading': 'Registrar una solicitud recibida por otro canal',
  'internal.intro': 'Formulario interno de registro de consultas, dudas, quejas o reclamos ingresados por otros canales distintos al formulario en línea (buzones, cajas, libros de reclamaciones, formularios físicos).',
  'internal.section.origin': 'Origen de la solicitud',
  'internal.channel': 'Canal de ingreso de la solicitud',
  'internal.registeredByOrg': 'Organización que registra la solicitud',
  'internal.presentedAt': 'Fecha de presentación del registro',
  'internal.presentedAtHint': 'Fecha en la que el solicitante presentó originalmente su registro.',
  'internal.attachmentsNote': 'Evidencias entregadas (describe brevemente los archivos o documentos)',
  'internal.consent': 'Confirmo que el solicitante fue informado del tratamiento de sus datos personales y dio su consentimiento en el registro físico.',
  'internal.submit': 'Registrar solicitud',
  'internal.created': 'Solicitud registrada con el código {code}. Entrega este código al solicitante para el seguimiento.',
  'internal.openRequest': 'Abrir la solicitud',

  // Error codes (backend)
  'error.END_BEFORE_START': 'La fecha de término es anterior a la fecha de inicio.',
  'error.EVENT_SUBTYPE_REQUIRED': 'Selecciona el tipo de evento.',
  'error.PARTICIPANTS_SUM_MISMATCH': 'Mujeres + hombres + otros géneros no coincide con el total de participantes.',
  'error.TEAM_EXCEEDS_PARTICIPANTS': 'El equipo técnico no puede superar el total de participantes.',
  'error.UNKNOWN_PROJECT': 'Proyecto desconocido.',
  'error.CHANNEL_NOT_ALLOWED': 'Canal no permitido para el registro interno.',
  'error.INVALID_ATTACHMENT_KIND': 'Tipo de documento inválido.',
  'error.invalidUrl': 'Ingresa un enlace válido (https://…).',
  'error.invalidNumber': 'Ingresa un número válido.',
};

const fr: Record<string, string> = {
  'home.staff.heading': 'Équipe technique et organisations partenaires',
  'home.staff.intro': 'Enregistrement des événements de terrain et des demandes reçues par d’autres canaux (boîte, livre de réclamations, formulaire papier). Seul votre e-mail est demandé.',
  'home.staff.event': 'Enregistrer un événement',
  'home.staff.internal': 'Enregistrer une demande reçue par un autre canal',
  'home.staff.access': 'La revue et le suivi se font dans le back-office (compte Microsoft du projet).',
  'fev.reporterEmail': 'Votre e-mail',
  'fev.reporterEmailHint': 'E-mail de la personne qui déclare (équipe technique ACOPAGRO, ORO VERDE, PUR, CECAT, VOLCAFE).',
  'fev.public.saved': 'Événement enregistré. Vous pouvez joindre les pièces justificatives pendant les 24 prochaines heures.',
  'fev.public.another': 'Enregistrer un autre événement',
  'admin.events': 'Événements',
  'admin.newEvent': 'Nouvel événement',
  'admin.newInternalRequest': 'Enregistrer une demande (autre canal)',
  'admin.export': 'Exporter Excel',
  'admin.exporting': 'Génération du fichier…',
  'admin.delete': 'Supprimer',
  'admin.confirmDelete': 'Supprimer cet enregistrement ? Cette action est irréversible.',
  'admin.created': 'Enregistrement créé',
  'admin.back': 'Retour',
  'admin.col.project': 'Projet',
  'admin.col.org': 'Entité',
  'admin.col.component': 'Composante',
  'admin.col.dates': 'Dates',
  'admin.col.name': 'Nom',
  'admin.col.participants': 'Participants',
  'admin.col.channel': 'Canal',
  'admin.filters.from': 'Du',
  'admin.filters.to': 'Au',
  'admin.stats.events': 'Événements',
  'admin.stats.participants': 'Participants',
  'admin.stats.hours': 'Heures effectives',
  'admin.stats.byComponent': 'Par composante',
  'admin.stats.byOrg': 'Par entité',

  'org.ACOPAGRO': 'ACOPAGRO',
  'org.ORO_VERDE': 'ORO VERDE',
  'org.PUR': 'PUR',
  'org.CECAT': 'CECAT',
  'org.VOLCAFE': 'VOLCAFE',
  'channel.ONLINE_FORM': 'Formulaire en ligne',
  'channel.COMPLAINTS_BOOK': 'Livre de réclamations (organisations partenaires ou pépinières)',
  'channel.MAILBOX': 'Boîte MACQR (organisations partenaires ou pépinières)',
  'channel.EVENT_BOX': 'Urne MACQR en événement (ateliers ou réunions)',
  'channel.PAPER_FORM': 'Formulaire papier recueilli en visite de terrain',
  'channel.WEBSITE_EMAIL': 'Site web ou courriel institutionnel',

  'component.SOCIALIZACION': 'Socialisation',
  'component.SIEMBRA': 'Plantation',
  'component.MANEJO_FORESTAL': 'Gestion forestière',
  'component.GESTION_TECNICA': 'Gestion technique du projet',
  'socialization.GRUPAL': 'Collective',
  'socialization.INDIVIDUAL': 'Individuelle',
  'technical.COMITE_FORESTAL': 'Comité forestier',
  'technical.TALLER_CAPACITACION': 'Atelier de formation',
  'technical.REUNION_TECNICA': 'Réunion technique et/ou suivi',
  'technical.CARBON_REVENUE_SHARING': 'Carbon Revenue Sharing',
  'forestPlan.CAPACITACION_BRIGADA': 'Formation de la brigade forestière',
  'forestPlan.CAPACITACION_SAF': 'Formation à la gestion des SAF',
  'modality.PRESENCIAL': 'Présentiel',
  'modality.VIRTUAL': 'Virtuel',
  'modality.AMBOS': 'Mixte',
  'attachKind.ATTENDANCE_LIST': 'Liste de présence',
  'attachKind.PHOTO': 'Photos de l’événement',
  'attachKind.VIDEO': 'Vidéo de l’événement',
  'attachKind.DOCUMENT': 'Comptes rendus, rapports ou documents',
  'attachKind.SOCIALIZATION_SHEET': 'Fiche de socialisation (visite individuelle)',

  'fev.heading.new': 'Enregistrer un événement',
  'fev.heading.edit': 'Événement',
  'fev.intro': 'Ne déclarer qu’un événement terminé. Prévoir la liste de présence scannée, 3 photos maximum et, le cas échéant, la vidéo ou le compte rendu.',
  'fev.section.general': 'Données générales',
  'fev.section.report': 'Déroulement',
  'fev.section.location': 'Localisation',
  'fev.section.details': 'Détails de l’événement',
  'fev.section.participants': 'Participants',
  'fev.section.team': 'Participation de l’équipe technique du projet',
  'fev.section.conclusions': 'Résultats',
  'fev.section.attachments': 'Pièces justificatives',
  'fev.project': 'Projet',
  'fev.reportingOrg': 'Entité déclarante',
  'fev.contractWave': 'Contrat ou vague concernée (année)',
  'fev.component': 'Événement lié à',
  'fev.socializationType': 'Type de socialisation',
  'fev.technicalType': 'Type d’événement : gestion technique',
  'fev.forestPlanType': 'Type d’événement : plan de gestion forestière',
  'fev.startDate': 'Date de début',
  'fev.endDate': 'Date de fin',
  'fev.modality': 'Modalité',
  'fev.province': 'Province',
  'fev.district': 'District',
  'fev.sector': 'Secteur ou communauté',
  'fev.sectorHint': 'Communauté ou secteur où s’est déroulé l’événement.',
  'fev.otherPlace': 'Autre lieu, préciser',
  'fev.name': 'Nom de l’événement',
  'fev.effectiveHours': 'Heures effectives',
  'fev.effectiveHoursHint': 'Total d’heures effectives selon l’agenda. Nombre seul (ex. 2.5).',
  'fev.objective': 'Objectif de l’événement',
  'fev.participantsTotal': 'Nombre total de participants',
  'fev.participantsWomen': 'Nombre de femmes',
  'fev.participantsMen': 'Nombre d’hommes',
  'fev.participantsOther': 'Autres genres',
  'fev.participantsHint': 'Femmes + hommes + autres genres doit être égal au total.',
  'fev.teamHelp': 'Membres de l’équipe technique du projet (toutes entités) présents. Ils sont déjà comptés dans le total des participants.',
  'fev.teamWomen': 'Femmes',
  'fev.teamMen': 'Hommes',
  'fev.teamTotal': 'Total équipe technique',
  'fev.conclusions': 'Conclusions, accords ou recommandations',
  'fev.conclusionsHint': 'De façon concise : conclusions principales, accords, recommandations des participants.',
  'fev.attachments.help': 'PDF, JPG ou PNG jusqu’à 10 Mo ; vidéo MP4 jusqu’à 100 Mo. Un fichier Google Drive peut aussi être référencé par son lien.',
  'fev.attachments.kind': 'Type de document',
  'fev.attachments.upload': 'Téléverser',
  'fev.attachments.addLink': 'Ajouter un lien',
  'fev.attachments.linkUrl': 'Lien (https://…)',
  'fev.attachments.saveFirst': 'Enregistrez l’événement pour pouvoir joindre des documents.',
  'fev.attachments.empty': 'Aucune pièce jointe.',
  'fev.attachments.open': 'Ouvrir',
  'fev.reportedBy': 'Enregistré par',
  'fev.legacy': 'Importé du formulaire Google (saisie d’origine : {email}, {date})',
  'fev.save': 'Enregistrer l’événement',
  'fev.saved': 'Événement enregistré',

  'internal.heading': 'Enregistrer une demande reçue par un autre canal',
  'internal.intro': 'Formulaire interne d’enregistrement des consultations, doutes, plaintes ou réclamations arrivées par un canal autre que le formulaire en ligne (boîtes, urnes, livres de réclamations, formulaires papier).',
  'internal.section.origin': 'Origine de la demande',
  'internal.channel': 'Canal d’entrée de la demande',
  'internal.registeredByOrg': 'Organisation qui enregistre la demande',
  'internal.presentedAt': 'Date de dépôt d’origine',
  'internal.presentedAtHint': 'Date à laquelle le demandeur a déposé sa demande.',
  'internal.attachmentsNote': 'Éléments remis (décrire brièvement les fichiers ou documents)',
  'internal.consent': 'Je confirme que le demandeur a été informé du traitement de ses données personnelles et a donné son consentement sur le formulaire physique.',
  'internal.submit': 'Enregistrer la demande',
  'internal.created': 'Demande enregistrée avec le code {code}. Remettez ce code au demandeur pour le suivi.',
  'internal.openRequest': 'Ouvrir la demande',

  'error.END_BEFORE_START': 'La date de fin est antérieure à la date de début.',
  'error.EVENT_SUBTYPE_REQUIRED': 'Sélectionnez le type d’événement.',
  'error.PARTICIPANTS_SUM_MISMATCH': 'Femmes + hommes + autres genres ne correspond pas au total.',
  'error.TEAM_EXCEEDS_PARTICIPANTS': 'L’équipe technique ne peut pas dépasser le total des participants.',
  'error.UNKNOWN_PROJECT': 'Projet inconnu.',
  'error.CHANNEL_NOT_ALLOWED': 'Canal non autorisé pour l’enregistrement interne.',
  'error.INVALID_ATTACHMENT_KIND': 'Type de document invalide.',
  'error.invalidUrl': 'Saisissez un lien valide (https://…).',
  'error.invalidNumber': 'Saisissez un nombre valide.',
};

const en: Record<string, string> = {
  'home.staff.heading': 'Technical team and partner organisations',
  'home.staff.intro': 'Register field events and requests received through other channels (mailbox, complaints book, paper form). Only your e-mail is required.',
  'home.staff.event': 'Register an event',
  'home.staff.internal': 'Register a request received through another channel',
  'home.staff.access': 'Review and follow-up happen in the back-office (project Microsoft account).',
  'fev.reporterEmail': 'Your e-mail',
  'fev.reporterEmailHint': 'E-mail of the person reporting (technical team of ACOPAGRO, ORO VERDE, PUR, CECAT, VOLCAFE).',
  'fev.public.saved': 'Event registered. You can attach the evidence during the next 24 hours.',
  'fev.public.another': 'Register another event',
  'admin.events': 'Events',
  'admin.newEvent': 'New event',
  'admin.newInternalRequest': 'Register a request (other channel)',
  'admin.export': 'Export Excel',
  'admin.exporting': 'Generating the file…',
  'admin.delete': 'Delete',
  'admin.confirmDelete': 'Delete this record? This cannot be undone.',
  'admin.created': 'Record created',
  'admin.back': 'Back',
  'admin.col.project': 'Project',
  'admin.col.org': 'Entity',
  'admin.col.component': 'Component',
  'admin.col.dates': 'Dates',
  'admin.col.name': 'Name',
  'admin.col.participants': 'Participants',
  'admin.col.channel': 'Channel',
  'admin.filters.from': 'From',
  'admin.filters.to': 'To',
  'admin.stats.events': 'Events',
  'admin.stats.participants': 'Participants',
  'admin.stats.hours': 'Effective hours',
  'admin.stats.byComponent': 'By component',
  'admin.stats.byOrg': 'By entity',

  'org.ACOPAGRO': 'ACOPAGRO',
  'org.ORO_VERDE': 'ORO VERDE',
  'org.PUR': 'PUR',
  'org.CECAT': 'CECAT',
  'org.VOLCAFE': 'VOLCAFE',
  'channel.ONLINE_FORM': 'Online form',
  'channel.COMPLAINTS_BOOK': 'Complaints book (partner organisations or nurseries)',
  'channel.MAILBOX': 'MACQR mailbox (partner organisations or nurseries)',
  'channel.EVENT_BOX': 'MACQR box at events (workshops or meetings)',
  'channel.PAPER_FORM': 'Paper form collected during field visits',
  'channel.WEBSITE_EMAIL': 'Website or institutional e-mail',

  'component.SOCIALIZACION': 'Socialisation',
  'component.SIEMBRA': 'Planting',
  'component.MANEJO_FORESTAL': 'Forest management',
  'component.GESTION_TECNICA': 'Technical project management',
  'socialization.GRUPAL': 'Group',
  'socialization.INDIVIDUAL': 'Individual',
  'technical.COMITE_FORESTAL': 'Forest committee',
  'technical.TALLER_CAPACITACION': 'Training workshop',
  'technical.REUNION_TECNICA': 'Technical and/or monitoring meeting',
  'technical.CARBON_REVENUE_SHARING': 'Carbon Revenue Sharing',
  'forestPlan.CAPACITACION_BRIGADA': 'Forest brigade training',
  'forestPlan.CAPACITACION_SAF': 'Agroforestry (SAF) management training',
  'modality.PRESENCIAL': 'In person',
  'modality.VIRTUAL': 'Online',
  'modality.AMBOS': 'Hybrid',
  'attachKind.ATTENDANCE_LIST': 'Attendance list',
  'attachKind.PHOTO': 'Event photos',
  'attachKind.VIDEO': 'Event video',
  'attachKind.DOCUMENT': 'Minutes, reports or documents',
  'attachKind.SOCIALIZATION_SHEET': 'Socialisation sheet (individual visit)',

  'fev.heading.new': 'Register an event',
  'fev.heading.edit': 'Event',
  'fev.intro': 'Only report a finished event. Have the scanned attendance list, up to 3 photos and, if applicable, the video or minutes at hand.',
  'fev.section.general': 'General data',
  'fev.section.report': 'Event report',
  'fev.section.location': 'Location',
  'fev.section.details': 'Event details',
  'fev.section.participants': 'Participants',
  'fev.section.team': 'Project technical team participation',
  'fev.section.conclusions': 'Outcomes',
  'fev.section.attachments': 'Evidence',
  'fev.project': 'Project',
  'fev.reportingOrg': 'Reporting entity',
  'fev.contractWave': 'Related contract or wave (year)',
  'fev.component': 'Event related to',
  'fev.socializationType': 'Socialisation type',
  'fev.technicalType': 'Event type: technical management',
  'fev.forestPlanType': 'Event type: forest management plan',
  'fev.startDate': 'Start date',
  'fev.endDate': 'End date',
  'fev.modality': 'Modality',
  'fev.province': 'Province',
  'fev.district': 'District',
  'fev.sector': 'Sector or community',
  'fev.sectorHint': 'Community or sector where the event took place.',
  'fev.otherPlace': 'Other place, specify',
  'fev.name': 'Event name',
  'fev.effectiveHours': 'Effective hours',
  'fev.effectiveHoursHint': 'Total effective hours per the agenda. Number only (e.g. 2.5).',
  'fev.objective': 'Event objective',
  'fev.participantsTotal': 'Total participants',
  'fev.participantsWomen': 'Women',
  'fev.participantsMen': 'Men',
  'fev.participantsOther': 'Other genders',
  'fev.participantsHint': 'Women + men + other genders must equal the total.',
  'fev.teamHelp': 'Members of the project technical team (all entities) who attended. They are already included in the participant total.',
  'fev.teamWomen': 'Women',
  'fev.teamMen': 'Men',
  'fev.teamTotal': 'Technical team total',
  'fev.conclusions': 'Conclusions, agreements or recommendations',
  'fev.conclusionsHint': 'Concisely: main conclusions, agreements reached, recommendations from participants.',
  'fev.attachments.help': 'PDF, JPG or PNG up to 10 MB; MP4 video up to 100 MB. A Google Drive file can also be referenced by its link.',
  'fev.attachments.kind': 'Document type',
  'fev.attachments.upload': 'Upload file',
  'fev.attachments.addLink': 'Add link',
  'fev.attachments.linkUrl': 'Link (https://…)',
  'fev.attachments.saveFirst': 'Save the event before attaching documents.',
  'fev.attachments.empty': 'No attachments.',
  'fev.attachments.open': 'Open',
  'fev.reportedBy': 'Registered by',
  'fev.legacy': 'Imported from the Google form (original entry: {email}, {date})',
  'fev.save': 'Save event',
  'fev.saved': 'Event saved',

  'internal.heading': 'Register a request received through another channel',
  'internal.intro': 'Internal form to register consultations, doubts, complaints or grievances that arrived through channels other than the online form (mailboxes, boxes, complaints books, paper forms).',
  'internal.section.origin': 'Origin of the request',
  'internal.channel': 'Channel the request came through',
  'internal.registeredByOrg': 'Organisation registering the request',
  'internal.presentedAt': 'Original submission date',
  'internal.presentedAtHint': 'Date the requester originally handed in the request.',
  'internal.attachmentsNote': 'Evidence handed in (briefly describe the files or documents)',
  'internal.consent': 'I confirm the requester was informed about the processing of their personal data and gave consent on the physical form.',
  'internal.submit': 'Register request',
  'internal.created': 'Request registered with code {code}. Give this code to the requester for tracking.',
  'internal.openRequest': 'Open the request',

  'error.END_BEFORE_START': 'The end date is before the start date.',
  'error.EVENT_SUBTYPE_REQUIRED': 'Select the event type.',
  'error.PARTICIPANTS_SUM_MISMATCH': 'Women + men + other genders does not match the total.',
  'error.TEAM_EXCEEDS_PARTICIPANTS': 'The technical team cannot exceed the total participants.',
  'error.UNKNOWN_PROJECT': 'Unknown project.',
  'error.CHANNEL_NOT_ALLOWED': 'Channel not allowed for internal registration.',
  'error.INVALID_ATTACHMENT_KIND': 'Invalid document type.',
  'error.invalidUrl': 'Enter a valid link (https://…).',
  'error.invalidNumber': 'Enter a valid number.',
};

export const EVENT_STRINGS: Record<Locale, Record<string, string>> = { es, fr, en };
